import bgLine from './backgrounds';
import classNames from 'classnames';
import React, { useContext, useEffect, useRef, useState } from 'react';
import 'swiper/css';
import 'swiper/css/autoplay';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import Button from '../../UI/button/Button';
import NextImage from '../../UI/nextImage/NextImage';
import { Title } from '../../UI/title/Title';
import { HeaderContext } from '../../providers/headerContextProvider/HeaderContextProvider';

import styles from './BundleSection.module.scss';

const thesisArr = [
    'All Themes',
    'Get Future Themes for Free',
    'Lifetime Updates & Access',
    'One‐time Purchase Fee',
];

const excerpt = `Receive an all-inclusive collection of current and future premium Ghost themes, 
                 complete with lifetime updates and access, all for a one-time purchase fee.`;

type TDirection = 'vertical' | 'horizontal';

const BundleSection = ({
    className,
    showLink,
    isMain,
}: {
    className: string;
    showLink?: boolean;
    isMain?: boolean;
}) => {
    const sizes = `(max-width: 376px) 280px, (max-width: 414px) 320px, 
                    (max-width: 480px) 360px, (max-width: 576px) 400px, 
                    (max-width: 1024px) 500px, 26.46vw`;

    const sectionRef = useRef<HTMLElement>(null);
    const [isFullyScrolled, setIsFullyScrolled] = useState(false);
    const { setIsBundleScrolled } = useContext(HeaderContext);
    const [breakpointKey, setBreakpointKey] = useState<'mobile' | 'desktop'>(
        typeof window !== 'undefined' && window.innerWidth >= 1025 ? 'desktop' : 'mobile'
    );

    useEffect(() => {
        const handleResize = () => {
            setBreakpointKey(window.innerWidth >= 1025 ? 'desktop' : 'mobile');
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [breakpointKey]);

    useEffect(() => {
        const handleScroll = () => {
            const section = sectionRef.current;
            if (section) {
                const { bottom } = section.getBoundingClientRect();
                const isSectionFullyVisible = bottom >= 0;
                setIsFullyScrolled(!isSectionFullyVisible);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        isFullyScrolled ? setIsBundleScrolled(true) : setIsBundleScrolled(false);
    }, [isFullyScrolled, setIsBundleScrolled]);

    return (
        <section className={`${styles.bundle__section} ${className}`} ref={sectionRef}>
            <Swiper
                modules={[Autoplay]}
                className={`${styles.swiper} ${styles.swiper__left} gallery-autoplay-swiper`}
                slidesPerView={'auto'}
                initialSlide={0}
                allowTouchMove={false}
                shortSwipes={false}
                longSwipes={false}
                loop={true}
                autoplay={{
                    disableOnInteraction: false,
                    pauseOnMouseEnter: false,
                    delay: 0,
                }}
                direction={breakpointKey === 'desktop' ? 'vertical' : 'horizontal'}
                breakpoints={{
                    0: {
                        spaceBetween: 12,
                    },
                    481: {
                        spaceBetween: 16,
                    },
                }}
                speed={15000}>
                {bgLine.map((bg, i) => {
                    return (
                        <SwiperSlide className={styles.column__block} key={`background-left-${i}`}>
                            <NextImage
                                src={`${bg.src}`}
                                alt={`Background ${i}`}
                                fill
                                sizes={sizes}
                                priority
                            />
                        </SwiperSlide>
                    );
                })}
            </Swiper>
            <article className={styles.section__info}>
                <div className={styles.info__top}>
                    <Title
                        text="Save Big with the Ghost Theme Bundle"
                        size={isMain ? 'xl' : 'lg'}
                        className={classNames(styles.heading, isMain ? styles.heading__main : '')}
                    />

                    <p className={`excerpt body-lg excerpt-wrap ${styles.excerpt}`}>{excerpt}</p>

                    <div className={styles.buttons}>
                        <div className={classNames(styles.highlight, 'caveat')}>
                            Limited Offer
                            <svg
                                width="41"
                                height="35"
                                viewBox="0 0 41 35"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M1.74587 1.48694C0.863602 6.25999 1.81449 12.857 5.85758 18.5874C9.90066 24.3178 18.5734 26.8989 27.1262 27.7154C31.1942 28.1037 34.8488 27.6155 38.8168 27.4972C41.4851 27.4177 36.0504 29.2832 35.5299 29.4876C33.0258 30.4704 26.0972 34.2627 28.4478 33.0592C31.6194 31.4353 34.9408 30.0732 38.2068 28.5878C40.8805 27.3719 38.7942 28.0261 36.5125 26.7066C34.9979 25.8307 32.197 24.1729 32.8529 22.5896"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </div>
                        <Button
                            ariaLabelledby="buttonText"
                            color="sale"
                            target
                            buttonUrl="https://highfivethemes.lemonsqueezy.com/checkout/buy/015cba04-c2be-4d1b-b20d-a252d477dedc">
                            <span id="buttonText">Buy theme bundle</span>
                        </Button>
                        {showLink && (
                            <Button
                                buttonUrl="/bundle"
                                ariaLabel="Learn more about purchasing bundle"
                                color="link"
                                className={styles.button}>
                                <span aria-hidden="true">Learn more</span>
                                <svg
                                    aria-hidden="true"
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 18 18"
                                    fill="none">
                                    <path
                                        d="M6.43674 5.19931L12.7123 5.28769M12.7123 5.28769L12.8007 11.5633M12.7123 5.28769L5.28769 12.7123"
                                        stroke="currentColor"
                                        strokeWidth="1.7"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </Button>
                        )}
                    </div>
                </div>

                <ul className={styles.thesis__list}>
                    {thesisArr.map((item, i) => {
                        return (
                            <li key={`${styles.item}-${i}`} className={`${styles.thesis__item}`}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="19"
                                    height="19"
                                    viewBox="0 0 19 19"
                                    fill="none">
                                    <path
                                        d="M3.3125 10.625L7.25 14.5625L16.25 5.5625"
                                        stroke="currentColor"
                                        strokeWidth="2.2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span>{item}</span>
                            </li>
                        );
                    })}
                </ul>
            </article>

            <Swiper
                modules={[Autoplay]}
                className={`${styles.swiper} ${styles.swiper__right} gallery-autoplay-swiper`}
                slidesPerView={'auto'}
                initialSlide={0}
                allowTouchMove={false}
                direction={breakpointKey === 'desktop' ? 'vertical' : 'horizontal'}
                shortSwipes={false}
                longSwipes={false}
                loop={true}
                autoplay={{
                    disableOnInteraction: false,
                    pauseOnMouseEnter: false,
                    delay: 0,
                    reverseDirection: true,
                }}
                breakpoints={{
                    0: {
                        spaceBetween: 12,
                    },
                    481: {
                        spaceBetween: 16,
                    },
                }}
                speed={15000}>
                {bgLine.reverse().map((bg, i) => {
                    return (
                        <SwiperSlide className={styles.column__block} key={`background-right-${i}`}>
                            <NextImage
                                src={`${bg.src}`}
                                alt={`Background ${i}`}
                                fill
                                sizes={sizes}
                                priority
                            />
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </section>
    );
};

export default BundleSection;
